import { Temporal } from '@js-temporal/polyfill'
import { parseTemporal, toPlainDate } from './date'
import { safeParseFloat } from './number'

const numberFormatter = new Intl.NumberFormat('en-US')

/**
 * Format a number with US locale formatting and optional custom options.
 * Uses standard thousand separators (commas) and decimal points.
 *
 * @param value - The number to format
 * @param formatOptions - Optional Intl.NumberFormatOptions to customize formatting behavior
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234.56) // "1,234.56"
 * formatNumber(1234.56, { formatOptions: { minimumFractionDigits: 0 } }) // "1,235"
 */
export function formatNumber(
  value: number,
  { formatOptions }: { formatOptions?: Intl.NumberFormatOptions } = {},
) {
  if (formatOptions === undefined) {
    return numberFormatter.format(value)
  }

  return new Intl.NumberFormat('en-US', formatOptions).format(value)
}

export function formatMultiplier(value: number) {
  return `${formatNumber(value, {
    formatOptions: { maximumFractionDigits: 2, minimumFractionDigits: 2 },
  })}x`
}

const RANGE_NUMBER_SCALES = [
  { divisor: 1_000_000_000, suffix: 'B', threshold: 1_000_000_000 },
  { divisor: 1_000_000, suffix: 'M', threshold: 1_000_000 },
  { divisor: 1_000, suffix: 'K', threshold: 1_000 },
]

/**
 * Format a number with one compact scale chosen from the range maximum.
 * Every value in the same chart uses that scale, like a shared axis tick unit.
 *
 * @example
 * formatNumberForRange({ maxValue: 800, value: 450 }) // "450"
 * formatNumberForRange({ maxValue: 50_000, value: 450 }) // "0.5K"
 * formatNumberForRange({ maxValue: 2_700_000, value: 2_785_000 }) // "2.8M"
 */
export function formatNumberForRange({
  maxValue,
  value,
}: {
  maxValue: number
  value: number
}) {
  const magnitude = Math.abs(maxValue)
  const scale = RANGE_NUMBER_SCALES.find((candidate) => {
    return magnitude >= candidate.threshold
  })
  if (scale === undefined) {
    return formatNumber(Math.round(value), {
      formatOptions: { maximumFractionDigits: 0 },
    })
  }

  const scaled = value / scale.divisor
  const maximumFractionDigits = Math.abs(scaled) >= 10 ? 0 : 1
  const formatted = formatNumber(scaled, {
    formatOptions: {
      maximumFractionDigits,
      minimumFractionDigits: 0,
    },
  })
  if (formatted !== '0' && formatted !== '-0') {
    return `${formatted}${scale.suffix}`
  }

  if (value === 0) {
    return '0'
  }

  const smallestDisplayedUnit = 10 ** -maximumFractionDigits
  const bound = formatNumber(smallestDisplayedUnit, {
    formatOptions: {
      maximumFractionDigits,
      minimumFractionDigits: 0,
    },
  })
  return `<${bound}${scale.suffix}`
}

const percentageFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  style: 'percent',
})

const roundPercentageFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
  style: 'percent',
})

interface FormatPercentageOptions {
  /**
   * @default false
   */
  shouldShowDecimals?: boolean
}

/**
 * Format a number as a percentage with US locale formatting.
 * The value should be a decimal (e.g., 0.5 for 50%).
 *
 * @param value - The decimal number to format as a percentage (e.g., 0.5 for 50%)
 * @param shouldShowDecimals - Whether to show up to 2 decimal places. If false, rounds to nearest integer percentage
 * @returns Formatted percentage string with % symbol
 *
 * @example
 * formatPercentage(0.5) // "50%"
 * formatPercentage(0.567) // "57%"
 * formatPercentage(8.567) // "857%"
 * formatPercentage('0.567', { shouldShowDecimals: true }) // "56.7%"
 * formatPercentage('not a number') // "0%"
 * formatPercentage(0.12345, { shouldShowDecimals: true }) // "12.35%"
 */
export function formatPercentage(
  value: number | string,
  { shouldShowDecimals = false }: FormatPercentageOptions = {},
) {
  return (
    shouldShowDecimals ? percentageFormatter : roundPercentageFormatter
  ).format(safeParseFloat(value, 0))
}

export interface FormatCurrencyOptions {
  /**
   * @default 'auto'
   */
  decimalDisplay?: 'always' | 'never' | 'necessary' | 'auto'

  /**
   * @default 'full'
   */
  notation?: 'compact' | 'full'
}

/**
 * Format a number or string as US currency (USD).
 * Supports both full and compact notations, with intelligent decimal handling.
 *
 * @param _value - The numeric value or string to format as currency
 * @param decimalDisplay - Controls decimal display behavior:
 *   - 'auto': Shows 2 decimals for values < 1000, none for larger values
 *   - 'always': Always shows 2 decimal places
 *   - 'never': Never shows decimal places (will round to nearest integer)
 *   - 'necessary': Shows up to 2 decimals only when needed
 * @param notation - Output format:
 *   - 'full': Full number format (e.g., "$1,234.56")
 *   - 'compact': Compact notation with abbreviations (e.g., "$1.2K")
 * @returns Formatted currency string with $ symbol
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1234.56, { notation: 'compact' }) // "$1.2K"
 * formatCurrency(50000, { notation: 'full' }) // "$50,000"
 * formatCurrency(50000, { decimalDisplay: 'always' }) // "$50,000.00"
 * formatCurrency("123.45") // "$123.45" (handles string input)
 */
export function formatCurrency(
  _value: number | string,
  { decimalDisplay = 'auto', notation = 'full' }: FormatCurrencyOptions = {},
) {
  const value = safeParseFloat(_value, 0)

  if (notation === 'compact') {
    return new Intl.NumberFormat('en-US', {
      currency: 'USD',
      notation: 'compact',
      style: 'currency',
    }).format(value)
  }

  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits:
      decimalDisplay === 'never'
        ? 0
        : decimalDisplay === 'always' ||
            decimalDisplay === 'necessary' ||
            value < 1000
          ? 2
          : 0,
    minimumFractionDigits:
      decimalDisplay === 'never'
        ? 0
        : decimalDisplay === 'always' ||
            (decimalDisplay === 'auto' && value < 1000)
          ? 2
          : 0,
    style: 'currency',
  }).format(value)
}

interface BaseDateFormatOptions {
  /**
   * Value to return when the date is invalid/null.
   * - If a string (or omitted, defaulting to "-"), return type is `string`
   * - If `null`, return type is `string | null`
   * @default '-'
   */
  fallback?: string | null

  /**
   * @default 'verbose'
   */
  format?:
    | 'numeric'
    | 'verbose'
    | 'month'
    | 'month_short'
    | 'month_long'
    | 'weekday'

  /**
   * if the date is in the current year, don't show the year
   * @default false
   */
  shouldSkipCurrentYear?: boolean
}

export interface DateFormatOptionsWithFallback extends BaseDateFormatOptions {
  /**
   * @default '-'
   */
  fallback?: string
}

interface DateFormatOptionsNullFallback extends BaseDateFormatOptions {
  fallback: null
}

type DateFormatOptions =
  | DateFormatOptionsWithFallback
  | DateFormatOptionsNullFallback

type AnyDate = string | Date | Temporal.PlainDate

export function formatDate(
  value: AnyDate,
  options: DateFormatOptionsNullFallback,
): string | null
export function formatDate(
  value: AnyDate,
  options?: DateFormatOptionsWithFallback,
): string
export function formatDate(
  value: AnyDate,
  {
    fallback = '-',
    format = 'verbose',
    shouldSkipCurrentYear = false,
  }: DateFormatOptions = {},
): string | null {
  // Convert PlainDate to Date for formatting
  const plainDate = toPlainDate(value)
  if (plainDate === null) {
    return fallback
  }

  if (format === 'numeric') {
    return plainDate.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  if (isMonthDateFormat(format)) {
    return formatPlainDateMonth({ format, plainDate, shouldSkipCurrentYear })
  }

  if (format === 'weekday') {
    if (
      shouldSkipCurrentYear &&
      plainDate.year === Temporal.Now.plainDateISO().year
    ) {
      return plainDate.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })
    }

    return plainDate.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
      year: 'numeric',
    })
  }

  if (
    shouldSkipCurrentYear &&
    plainDate.year === Temporal.Now.plainDateISO().year
  ) {
    return plainDate.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
    })
  }

  return plainDate.toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const MONTH_DATE_FORMATS: ReadonlySet<string> = new Set([
  'month',
  'month_long',
  'month_short',
])

function isMonthDateFormat(
  format: string,
): format is 'month' | 'month_long' | 'month_short' {
  return MONTH_DATE_FORMATS.has(format)
}

function formatPlainDateMonth({
  format,
  plainDate,
  shouldSkipCurrentYear,
}: {
  format: 'month' | 'month_long' | 'month_short'
  plainDate: Temporal.PlainDate
  shouldSkipCurrentYear: boolean
}) {
  if (format === 'month_short') {
    return plainDate.toLocaleString('en-US', {
      month: 'short',
    })
  }

  if (format === 'month_long') {
    return plainDate.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    })
  }

  if (
    shouldSkipCurrentYear &&
    plainDate.year === Temporal.Now.plainDateISO().year
  ) {
    return plainDate.toLocaleString('en-US', {
      month: 'short',
    })
  }

  return plainDate.toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

export function formatDateOfBirth(value: string | null | undefined) {
  if (!value) {
    return null
  }

  return formatDate(value, { fallback: value, format: 'numeric' })
}

export function formatDateRange(
  from: AnyDate,
  to: AnyDate,
  options: DateFormatOptionsNullFallback,
): string | null
export function formatDateRange(
  from: AnyDate,
  to: AnyDate,
  options?: DateFormatOptionsWithFallback,
): string
export function formatDateRange(
  from: AnyDate,
  to: AnyDate,
  { fallback = '-', ...rest }: DateFormatOptions = {},
): string | null {
  if (fallback === null) {
    const formattedFrom = formatDate(from, { fallback, ...rest })
    const formattedTo = formatDate(to, { fallback, ...rest })

    return [formattedFrom, formattedTo].filter(Boolean).join(' - ') || fallback
  }

  const formattedFrom = formatDate(from, { fallback, ...rest })
  const formattedTo = formatDate(to, { fallback, ...rest })

  return (
    [
      formattedFrom !== fallback && formattedFrom,
      formattedTo !== fallback && formattedTo,
    ]
      .filter(Boolean)
      .join(' - ') || fallback
  )
}

interface FormatWeekRangeOptions {
  length?: 'business' | 'calendar'
}

export function formatWeekRange(
  start: Temporal.PlainDate,
  { length = 'calendar' }: FormatWeekRangeOptions = {},
) {
  return formatDateRange(
    start,
    start.add({ days: length === 'business' ? 4 : 6 }),
    {
      shouldSkipCurrentYear: true,
    },
  )
}

/**
 * Format the time-of-day portion of a timestamp (e.g. `2:30 PM`)
 */
export function formatTimeOfDay(value: string): string | null {
  const temporalObject = parseTemporal(value)
  if (temporalObject === null) {
    return null
  }

  if (!(temporalObject instanceof Temporal.PlainDateTime)) {
    return null
  }

  return temporalObject.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

interface FormatTimestampOptions {
  /**
   * - 'full': Shows full date and time (e.g., "Jan 15, 2024 at 2:30 PM")
   * - 'adaptive': Smart formatting based on recency:
   *   - Today: time only (e.g., "2:30 PM")
   *   - This year: Jan 12
   *   - Previous years: Jan 12, 2024
   * @default 'full'
   */
  format?: 'adaptive' | 'full'

  /**
   * @default Browser time zone
   */
  timeZone?: string
}

/**
 * Format a timestamp string into a human-readable format.
 * If the timestamp includes time, formats as "Jan 15, 2024 at 2:30 PM".
 * If only a date, formats as "Jan 15, 2024".
 *
 * @param value - The timestamp string to format (ISO 8601 format)
 * @param options - Formatting options
 * @returns Formatted timestamp string, or "-" if parsing fails
 * @throws Error if the temporal object type is unrecognized (should not happen in practice)
 *
 * @example
 * formatTimestamp('2024-01-15T14:30:00') // "Jan 15, 2024 at 2:30 PM"
 * formatTimestamp('2024-01-15') // "Jan 15, 2024"
 * formatTimestamp('invalid') // "-"
 * formatTimestamp('2024-01-15T14:30:00', { format: 'adaptive' }) // "2:30 PM" (if today), "Jan 15" (if this year), "01/15/24" (if previous year)
 * formatTimestamp('2024-01-15T14:30:00Z', { timeZone: 'UTC' }) // "Jan 15, 2024 at 2:30 PM"
 */
export function formatTimestamp(
  value: string,
  {
    format = 'full',
    timeZone = Temporal.Now.timeZoneId(),
  }: FormatTimestampOptions = {},
) {
  const temporalObject = parseTemporal(value, { timeZone })
  if (temporalObject === null) {
    return '-'
  }

  if (format === 'adaptive') {
    const now = Temporal.Now.plainDateISO(timeZone)
    const date =
      temporalObject instanceof Temporal.PlainDateTime
        ? temporalObject.toPlainDate()
        : temporalObject

    if (Temporal.PlainDate.compare(date, now) === 0) {
      if (temporalObject instanceof Temporal.PlainDateTime) {
        return temporalObject.toLocaleString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        })
      }

      return 'Today'
    }

    return formatDate(date, {
      shouldSkipCurrentYear: true,
    })
  }

  if (temporalObject instanceof Temporal.PlainDate) {
    return temporalObject.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  if (temporalObject instanceof Temporal.PlainDateTime) {
    return temporalObject.toLocaleString('en-US', {
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  throw new Error('Invalid temporal object')
}

/**
 * Format a timestamp as a relative time string (e.g., "2h ago", "3d ago").
 * Converts the timestamp to the local timezone and calculates the duration from now.
 * Uses progressively larger units (seconds, minutes, hours, days, weeks, months, years).
 *
 * Time unit breakdown:
 * - Less than 60 seconds: "just now"
 * - Less than 60 minutes: "Xm ago"
 * - Less than 24 hours: "Xh ago"
 * - Less than 7 days: "Xd ago"
 * - Less than 4 weeks: "Xw ago"
 * - Less than 12 months: "Xmo ago"
 * - 12+ months: "Xy ago"
 *
 * @param value - The timestamp string to format (ISO 8601 format)
 * @returns Relative time string, or "-" if parsing fails
 *
 * @example
 * formatRelativeTimestamp('2024-01-15T14:25:00') // "5m ago" (if current time is 14:30:00)
 * formatRelativeTimestamp('2024-01-14T14:30:00') // "1d ago" (if current date is Jan 15)
 * formatRelativeTimestamp('2023-01-15') // "1y ago" (if current date is Jan 2024)
 */
export function formatRelativeTimestamp(value: string) {
  // Convert input to ZonedDateTime in local timezone
  let temporalObject = parseTemporal(value)
  if (temporalObject === null) {
    return '-'
  }

  if (temporalObject instanceof Temporal.PlainDate) {
    temporalObject = temporalObject.toPlainDateTime(
      Temporal.PlainTime.from('00:00:00'),
    )
  }

  const zonedDateTime = temporalObject.toZonedDateTime(
    Temporal.Now.timeZoneId(),
  )

  const now = Temporal.Now.zonedDateTimeISO()
  const duration = zonedDateTime.until(now)

  const totalSeconds = Math.floor(Math.abs(duration.total('seconds')))

  if (totalSeconds < 60) {
    return 'just now'
  }

  const totalMinutes = Math.max(
    1,
    Math.floor(Math.abs(duration.total('minutes'))),
  )
  if (totalMinutes < 60) {
    return `${totalMinutes}m ago`
  }

  const totalHours = Math.max(1, Math.floor(Math.abs(duration.total('hours'))))
  if (totalHours < 24) {
    return `${totalHours}h ago`
  }

  const totalDays = Math.max(1, Math.floor(Math.abs(duration.total('days'))))
  if (totalDays < 7) {
    return `${totalDays}d ago`
  }

  const totalWeeks = Math.max(
    1,
    Math.floor(
      Math.abs(duration.total({ relativeTo: zonedDateTime, unit: 'weeks' })),
    ),
  )
  if (totalWeeks < 4) {
    return `${totalWeeks}w ago`
  }

  const totalMonths = Math.max(
    1,
    Math.floor(
      Math.abs(duration.total({ relativeTo: zonedDateTime, unit: 'months' })),
    ),
  )
  if (totalMonths < 12) {
    return `${totalMonths}mo ago`
  }

  const totalYears = Math.max(
    1,
    Math.floor(
      Math.abs(duration.total({ relativeTo: zonedDateTime, unit: 'years' })),
    ),
  )
  return `${totalYears}y ago`
}

interface FormatDurationOptions {
  format?: 'clock' | 'compact' | 'short'
}

export function formatDuration(
  seconds: number,
  { format = 'clock' }: FormatDurationOptions = {},
) {
  if (format === 'short') {
    const absoluteSeconds = Math.max(0, Math.round(seconds))
    const totalMinutes = Math.floor(absoluteSeconds / 60)
    const remainingSeconds = absoluteSeconds % 60
    const totalHours = Math.floor(totalMinutes / 60)
    const remainingMinutes = totalMinutes % 60

    if (totalHours > 0) {
      return `${totalHours}h ${remainingMinutes}m`
    }

    if (totalMinutes > 0) {
      return `${totalMinutes}m ${remainingSeconds}s`
    }

    return `${remainingSeconds}s`
  }

  if (format === 'compact') {
    const absoluteSeconds = Math.max(0, Math.floor(seconds))
    if (absoluteSeconds < 60) {
      return `${absoluteSeconds}s`
    }

    const totalMinutes = Math.floor(absoluteSeconds / 60)
    if (totalMinutes < 60) {
      return `${totalMinutes}m`
    }

    const totalHours = Math.floor(totalMinutes / 60)
    if (totalHours < 24) {
      return `${totalHours}h`
    }

    const totalDays = Math.floor(totalHours / 24)
    if (totalDays < 7) {
      return `${totalDays}d`
    }

    const totalWeeks = Math.floor(totalDays / 7)
    return `${totalWeeks}w`
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = Math.round(seconds) % 60
  const formattedSeconds = remainingSeconds.toString().padStart(2, '0')

  return [
    hours > 0 && hours.toString().padStart(2, '0'),
    hours > 0 ? minutes.toString().padStart(2, '0') : minutes.toString(),
    formattedSeconds,
  ]
    .filter(Boolean)
    .join(':')
}

interface PluralizeOptions {
  /**
   * if not provided, will default to adding 's' to the word
   */
  pluralWord?: string
}

/**
 * Pluralize a word based on a number and format the result with the number.
 * Automatically adds 's' to the word when the number is not exactly 1.
 * The number is formatted with thousand separators.
 *
 * @param num - The count to determine singular/plural form
 * @param word - The word to pluralize (assumed to use standard English pluralization with 's')
 * @returns Formatted string with number and pluralized word
 *
 * @example
 * pluralize(1, 'item') // "1 item"
 * pluralize(0, 'item') // "0 items"
 * pluralize(5, 'item') // "5 items"
 * pluralize(1000, 'claim') // "1,000 claims"
 *
 * @note This function only handles standard English pluralization (adding 's').
 * For irregular plurals (e.g., 'child' -> 'children'), pass the singular form
 * and handle irregular cases separately.
 */
export function pluralize(
  num: number,
  word: string,
  { pluralWord }: PluralizeOptions = {},
): string {
  return [
    formatNumber(num),
    pluralWord !== undefined && num !== 1
      ? pluralWord
      : `${word}${num === 1 ? '' : 's'}`,
  ].join(' ')
}

/**
 * Capitalize the first character of a string.
 * Leaves all other characters unchanged. Empty strings are returned as-is.
 *
 * @param str - The string to capitalize
 * @returns String with first character uppercased, or empty string if input is empty
 *
 * @example
 * capitalize('hello') // "Hello"
 * capitalize('hello world') // "Hello world" (only first character)
 * capitalize('HELLO') // "HELLO" (other characters unchanged)
 * capitalize('') // ""
 * capitalize('a') // "A"
 */
export function capitalize(str: string) {
  if (str.length === 0) {
    return str
  }

  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Format a byte size into a human-readable file size string with appropriate units.
 * Uses binary units (1024 bytes = 1 KB). Shows up to 2 decimal places.
 * Automatically selects the most appropriate unit (Bytes, KB, MB, GB).
 *
 * @param bytes - The size in bytes
 * @returns Formatted file size string with unit
 *
 * @example
 * formatFileSize(0) // "0 B"
 * formatFileSize(512) // "512 B"
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1536) // "1.5 KB"
 * formatFileSize(1048576) // "1 MB"
 * formatFileSize(1073741824) // "1 GB"
 * formatFileSize(1536000) // "1.46 MB"
 */
export function formatFileSize(bytes: number) {
  if (bytes === 0) {
    return '0 B'
  }

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function enumerate(
  values: Array<string>,
  options?: Intl.ListFormatOptions,
) {
  return new Intl.ListFormat('en-US', options).format(values)
}

/**
 * Convert a snake_case / kebab-case / SCREAMING_SNAKE enum string into a
 * sentence-case label for display when there is no curated schema entry to use
 * instead.
 *
 * @example
 * formatSnakeCaseLabel('IN_PROGRESS') // "In progress"
 * formatSnakeCaseLabel('warm_handoff') // "Warm handoff"
 * formatSnakeCaseLabel('payer-processing-delay') // "Payer processing delay"
 * formatSnakeCaseLabel('') // ""
 */
const SENTENCE_CASE_LABEL_ACRONYMS = new Set([
  'ar',
  'carc',
  'cms',
  'cpt',
  'dob',
  'dos',
  'ehr',
  'hcpcs',
  'id',
  'kpi',
  'npi',
  'phi',
  'qa',
  'tin',
])

export function formatSnakeCaseLabel(value: string): string {
  if (value.length === 0) {
    return value
  }

  const words = splitLabelWords(value.toLowerCase())
  const formattedWords = words.map((word, index) => {
    if (words.length > 1 && SENTENCE_CASE_LABEL_ACRONYMS.has(word)) {
      return word.toUpperCase()
    }

    if (index === 0) {
      return capitalize(word)
    }

    return word
  })

  return formattedWords.join(' ')
}

function splitLabelWords(value: string): Array<string> {
  const words: Array<string> = []
  let currentWord = ''
  for (const character of value) {
    if (character === '_' || character === '-' || character === ' ') {
      if (currentWord.length > 0) {
        words.push(currentWord)
        currentWord = ''
      }

      continue
    }

    currentWord += character
  }

  if (currentWord.length > 0) {
    words.push(currentWord)
  }

  return words
}
